import { useEffect, useState } from "react";
import type { Persona } from "@/lib/orgContext";

export const ACTIVE_PERSONA_STORAGE_KEY = "zindeal.activePersona";
const ACTIVE_PERSONA_EVENT = "zindeal:activePersonaChange";

function readStored(personas: Persona[]): Persona | null {
  try {
    const stored = localStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY) as Persona | null;
    if (stored && (personas as string[]).includes(stored)) return stored;
  } catch { }
  return null;
}

function writeLocal(p: Persona | null) {
  try {
    if (p) localStorage.setItem(ACTIVE_PERSONA_STORAGE_KEY, p);
    else localStorage.removeItem(ACTIVE_PERSONA_STORAGE_KEY);
  } catch { }
}

async function persistRemote(p: Persona | null): Promise<void> {
  try {
    await fetch("/api/auth/me/active-persona", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ persona: p }),
    });
  } catch {
    /* non-fatal: localStorage still has it for this browser */
  }
}

export function setActivePersonaStorage(p: Persona | null, opts?: { skipRemote?: boolean }) {
  writeLocal(p);
  if (!opts?.skipRemote) void persistRemote(p);
  try {
    window.dispatchEvent(new CustomEvent(ACTIVE_PERSONA_EVENT));
  } catch { }
}

export function useActivePersona(personas: Persona[], initial?: Persona | null) {
  const personasKey = personas.join(",");
  const [active, setActiveState] = useState<Persona | null>(() => {
    const local = readStored(personas);
    if (local) return local;
    if (initial && (personas as string[]).includes(initial)) return initial;
    return null;
  });

  const setActive = (p: Persona | null) => {
    setActivePersonaStorage(p);
    setActiveState(p);
  };

  useEffect(() => {
    const local = readStored(personas);
    if (local) {
      setActiveState(local);
      return;
    }
    if (initial && (personas as string[]).includes(initial)) {
      // Fresh browser: rehydrate localStorage from the saved profile preference
      // so other parts of the app reading localStorage stay consistent.
      writeLocal(initial);
      setActiveState(initial);
    } else {
      setActiveState(null);
    }
  }, [personasKey, initial]);

  useEffect(() => {
    const handler = () => setActiveState(readStored(personas));
    window.addEventListener(ACTIVE_PERSONA_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(ACTIVE_PERSONA_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, [personasKey]);

  const effective: Persona | null = active ?? (personas.length === 1 ? personas[0] : null);

  return { active, setActive, effective };
}
